import { createContext, useContext, useMemo, type ReactNode } from "react";
import { createTranslator, resolveLocale, type LocaleCode } from "@guestpilot/i18n";

export type T = (key: string, params?: Record<string, string | number>) => string;

interface I18n {
  locale: LocaleCode;
  t: T;
}

const Ctx = createContext<I18n | null>(null);

/** UI language: the user's GuestPilot locale (`/me`), else the Teams locale, else English (BR-234). Keys live in `packages/i18n`. */
export function I18nProvider({ locale, children }: { locale: string; children: ReactNode }) {
  const value = useMemo<I18n>(() => {
    const code = resolveLocale(locale);
    return { locale: code, t: createTranslator(code) };
  }, [locale]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useI18n(): I18n {
  const c = useContext(Ctx);
  if (!c) throw new Error("I18nProvider is missing");
  return c;
}

export const useT = (): T => useI18n().t;
